'use client'

import * as React from 'react'
import { Bell, BellOff, CalendarDays, Lock, MoreVertical, Trash2, Zap } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Chip, IconBtn, RMenu, Seg, pillBtnBase } from '@/components/roostr'
import { useKeywordsStore } from '@/lib/store'
import { FILTER_TAGS } from '@/lib/constants'
import { getSearchSummary } from '@/lib/roostr-utils'
import { useI18n } from '@/lib/i18n'
import type { SavedSearch } from '@/lib/types'

/** Effective per-search alert state: paused searches read as 'off'; legacy
 * stored 'weekly' displays as 'daily' (the backend coerces it on write). */
export type SearchCadence = 'off' | 'daily' | 'instant'
export function cadenceOf(s: SavedSearch): SearchCadence {
  if (!s.is_active) return 'off'
  return s.cadence === 'instant' ? 'instant' : 'daily'
}

// The prototype's `.rcard` hover treatment (theme.css:135-141), ported the
// same way listing-card.tsx does it.
const rcardHover =
  'transition-[box-shadow,transform,border-color] duration-200 ease-standard hover:-translate-y-0.5 hover:shadow-e2'

export interface SavedSearchCardProps {
  search: SavedSearch
  newCount: number
  /** Open the search — loads its saved criteria into /search. */
  onOpen: () => void
  /** Review the new matches (the "{n} new" pill). */
  onReview: () => void
  /** Kebab: pause/resume alerts (is_active). */
  onToggleActive: () => void
  /** Kebab: delete (with undo toast handled by the page). */
  onDelete: () => void
  /** Footer Seg: per-search alert cadence (off = pause). */
  onCadenceChange: (value: SearchCadence) => void
  /** Instant is Penthouse-only — renders a lock on the option when locked. */
  instantLocked: boolean
}

// Saved-search card — ported from the design's SavedSearchCard
// (searches.jsx:23-65): roostr pet-name headline (17/700), query line,
// "{n} new" primary-container pill, kebab menu (width 210), ≤4 keyword
// chips and a "Saved {date}" footer. Paused searches render dimmed with a
// paused hint in the footer (Q7 decision).
// User-directed 2026-07-16: the headline/body click OPENS THE SEARCH (loads
// its criteria into /search) instead of the design's detail-page nav — there
// is no separate saved-search results view. The "{n} new" pill routes to the
// What's-new queue scoped to this search.
export function SavedSearchCard({
  search: s,
  newCount,
  onOpen,
  onReview,
  onToggleActive,
  onDelete,
  onCadenceChange,
  instantLocked,
}: SavedSearchCardProps) {
  const { t, uiLanguage } = useI18n()
  const { filterTags } = useKeywordsStore()

  const tagLabel = React.useCallback(
    (tagId: string) => {
      const dynamic = filterTags.find((f) => f.id === tagId)
      if (dynamic) return dynamic.label
      const stat = FILTER_TAGS.find((f) => f.id === tagId)
      return stat?.label || tagId.replace(/_/g, ' ')
    },
    [filterTags]
  )

  // Headline = pet name when set; the query line then shows the search name.
  // Unnamed roostrs fall back to name + a criteria summary.
  const headline = s.roostr_name || s.name
  const queryLine = s.roostr_name ? s.name : getSearchSummary(s)

  // ≤4 chips: explicit feature tags first, then parsed keywords (searches.jsx:54-58).
  const chipIds = Array.from(new Set([...(s.tags || []), ...(s.keywords || [])])).slice(0, 4)

  const savedDate = new Date(s.created_at).toLocaleDateString(uiLanguage === 'cs' ? 'cs-CZ' : 'en-GB', {
    month: 'short',
    day: 'numeric',
    year: new Date(s.created_at).getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  })

  const plural = (n: number, one: string, few: string, many: string) =>
    n === 1 ? one : n >= 2 && n <= 4 ? few : many

  return (
    <article
      className={cn(
        'flex flex-col rounded-shape-lg border border-outline-variant bg-surface px-[18px] py-4',
        rcardHover,
        !s.is_active && 'opacity-[0.85]'
      )}
    >
      <div className="flex items-start gap-2">
        <div
          className="min-w-0 flex-1 cursor-pointer"
          onClick={onOpen}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onOpen()
          }}
          title={t('searches.openTitle', 'Open this search')}
        >
          <div className="text-[17px] font-bold tracking-[-0.2px] text-ink">{headline}</div>
          <div className="mt-[2px] truncate text-[12.5px] text-ink-secondary">{queryLine}</div>
        </div>
        {newCount > 0 ? (
          <button
            type="button"
            onClick={onReview}
            title={t('searches.reviewTitle', 'Review the new matches')}
            className={cn(
              pillBtnBase,
              'shrink-0 bg-primary-container px-[11px] py-[5px] text-[12px] font-bold text-on-primary-container'
            )}
          >
            {plural(
              newCount,
              t('searches.newOne', '1 new'),
              t('searches.newFew', '{n} new').replace('{n}', String(newCount)),
              t('searches.newMany', '{n} new').replace('{n}', String(newCount))
            )}
          </button>
        ) : null}
        <RMenu
          align="right"
          width={210}
          button={(open, toggle) => (
            <IconBtn
              size={30}
              onClick={toggle}
              title={t('searches.menuTitle', 'Search options')}
              aria-label={t('searches.menuTitle', 'Search options')}
              className={cn('text-ink-muted hover:bg-surface-high', open && 'bg-surface-high')}
            >
              <MoreVertical size={18} />
            </IconBtn>
          )}
          items={[
            {
              icon: s.is_active ? <BellOff size={17} /> : <Bell size={17} />,
              label: s.is_active
                ? t('searches.menuPause', 'Pause alerts')
                : t('searches.menuResume', 'Resume alerts'),
              onClick: onToggleActive,
            },
            {
              icon: <Trash2 size={17} />,
              label: t('searches.menuDelete', 'Delete search'),
              danger: true,
              onClick: onDelete,
            },
          ]}
        />
      </div>

      {chipIds.length ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {chipIds.map((k) => (
            <Chip key={k} label={tagLabel(k)} />
          ))}
        </div>
      ) : null}

      {/* Footer: saved date + per-search cadence (user-directed 2026-07-15 —
          the DB and workers run per-search `cadence`; 'off' = the same
          is_active pause as the kebab, so both affordances mirror one state). */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-[6px]">
        <span className="text-[12px] text-ink-muted">
          {t('searches.savedOn', 'Saved {date}').replace('{date}', savedDate)}
        </span>
        {/* Icons per the design's alerts Seg (searches.jsx: off · today · bolt);
            active tints: Off reads muted (silenced), Instant gets the
            primary-container pop. */}
        <Seg<SearchCadence>
          compact
          className="flex-none"
          value={cadenceOf(s)}
          onChange={onCadenceChange}
          options={[
            {
              value: 'off',
              label: t('searches.alertsOff', 'Off'),
              icon: <BellOff size={12} />,
              activeClassName: 'text-ink-muted',
            },
            {
              value: 'daily',
              label: t('searches.alertsDaily', 'Daily'),
              icon: <CalendarDays size={12} />,
            },
            {
              value: 'instant',
              label: t('searches.alertsInstant', 'Instant'),
              icon: instantLocked ? <Lock size={12} /> : <Zap size={12} />,
              activeClassName: 'bg-primary-container text-on-primary-container',
            },
          ]}
        />
      </div>
    </article>
  )
}
